/** CodeEditor のテキスト操作（純粋関数）。インデントは空白 4 つ */

export const INDENT = '    ';

export interface TextEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

function lineStartAt(value: string, pos: number): number {
  return value.lastIndexOf('\n', pos - 1) + 1;
}

/** Tab: 選択が 1 行内ならカーソル位置に空白 4 つ、複数行なら各行の先頭に足す */
export function indent(value: string, start: number, end: number): TextEdit {
  const selected = value.slice(start, end);
  if (!selected.includes('\n')) {
    const next = value.slice(0, start) + INDENT + value.slice(end);
    const pos = start + INDENT.length;
    return { value: next, selectionStart: pos, selectionEnd: pos };
  }
  const from = lineStartAt(value, start);
  // 選択の末尾が行頭ちょうどなら、その行は含めない
  const to = end > from && value[end - 1] === '\n' ? end - 1 : end;
  const block = value.slice(from, to);
  const lines = block.split('\n');
  const indented = lines.map((l) => INDENT + l).join('\n');
  return {
    value: value.slice(0, from) + indented + value.slice(to),
    selectionStart: start + INDENT.length,
    selectionEnd: end + INDENT.length * lines.length,
  };
}

/** Shift+Tab: 選択範囲の各行の先頭から空白を最大 4 つ取る */
export function outdent(value: string, start: number, end: number): TextEdit {
  const from = lineStartAt(value, start);
  const to = end > from && value[end - 1] === '\n' && end !== start ? end - 1 : end;
  const lines = value.slice(from, to).split('\n');
  let removedFirst = 0;
  let removedTotal = 0;
  const out = lines.map((l, i) => {
    const m = /^ {1,4}/.exec(l);
    const n = m ? m[0].length : 0;
    if (i === 0) removedFirst = n;
    removedTotal += n;
    return l.slice(n);
  });
  return {
    value: value.slice(0, from) + out.join('\n') + value.slice(to),
    selectionStart: Math.max(from, start - removedFirst),
    selectionEnd: Math.max(from, end - removedTotal),
  };
}

/** Enter: 前の行のインデントを引き継ぐ。`{` で終わる行なら 1 段深くする */
export function newlineWithIndent(value: string, start: number, end: number): TextEdit {
  const from = lineStartAt(value, start);
  const line = value.slice(from, start);
  const lead = /^[ \t]*/.exec(line)?.[0] ?? '';
  const extra = /\{\s*$/.test(line) ? INDENT : '';
  const insert = `\n${lead}${extra}`;
  const pos = start + insert.length;
  return {
    value: value.slice(0, start) + insert + value.slice(end),
    selectionStart: pos,
    selectionEnd: pos,
  };
}

export function lineCount(value: string): number {
  return value.split('\n').length;
}
